import { useState, useRef } from "react";
import type { MutableRefObject } from "react";
import type { DirectoryUserShape } from "@/api/assignments";

export function useUnsavedUsersWarning(setCurrentStep: (step: number) => void): {
  showUserSelectionDialog: boolean;
  selectedUsersToAdd: DirectoryUserShape[];
  setSelectedUsersToAdd: (users: DirectoryUserShape[]) => void;
  selectedUsersToRemove: DirectoryUserShape[];
  setSelectedUsersToRemove: (users: DirectoryUserShape[]) => void;
  addUsersAndContinueRef: MutableRefObject<(() => void) | null>;
  handleSelectedUsersChange: (users: DirectoryUserShape[]) => void;
  handleContinue: () => void;
  handleCancel: () => void;
  handleAddUsersAndContinue: () => void;
  checkUnsavedUsers: (currentStep: number, newStep: number, audienceStepIndex?: number) => boolean;
} {
  const [showUserSelectionDialog, setShowUserSelectionDialog] = useState(false);
  const [pendingStep, setPendingStep] = useState<number | null>(null);
  const [selectedUsersToAdd, setSelectedUsersToAdd] = useState<DirectoryUserShape[]>([]);
  const [selectedUsersToRemove, setSelectedUsersToRemove] = useState<DirectoryUserShape[]>([]);
  const addUsersAndContinueRef = useRef<(() => void) | null>(null);

  const handleSelectedUsersChange = (users: DirectoryUserShape[]) => {
    setSelectedUsersToAdd(users);
  };

  const handleContinue = () => {
    setShowUserSelectionDialog(false);
    if (pendingStep !== null) {
      setCurrentStep(pendingStep);
    }
    setPendingStep(null);
  };

  const handleCancel = () => {
    setShowUserSelectionDialog(false);
    setPendingStep(null);
  };

  const handleAddUsersAndContinue = () => {
    if (addUsersAndContinueRef.current) {
      try {
        addUsersAndContinueRef.current();
      } catch (error) {
        console.error("Failed to add users:", error);
        return;
      }
    }
    setShowUserSelectionDialog(false);
    if (pendingStep !== null) {
      setCurrentStep(pendingStep);
    }
    setPendingStep(null);
  };

  const checkUnsavedUsers = (
    currentStep: number,
    newStep: number,
    audienceStepIndex = 1
  ): boolean => {
    if (
      currentStep === audienceStepIndex &&
      newStep !== audienceStepIndex &&
      selectedUsersToAdd.length > 0
    ) {
      setPendingStep(newStep);
      setShowUserSelectionDialog(true);
      return true;
    }
    return false;
  };

  return {
    showUserSelectionDialog,
    selectedUsersToAdd,
    setSelectedUsersToAdd,
    selectedUsersToRemove,
    setSelectedUsersToRemove,
    addUsersAndContinueRef,
    handleSelectedUsersChange,
    handleContinue,
    handleCancel,
    handleAddUsersAndContinue,
    checkUnsavedUsers,
  };
}
